import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { read, utils } from 'xlsx/xlsx.mjs';  // Use the ESM version

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/UI/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/UI/dialog";
import { Input } from "@/components/UI/input";
import { Button } from "@/components/UI/button";
import { Label } from "@/components/UI/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/UI/select";
import { Alert, AlertDescription } from "@/components/UI/alert";
import {
  Search,
  Plus,
  Edit,
  Trash2,
  ChevronDown,
  ChevronUp,
  Upload
} from 'lucide-react';

const API_BASE_URL = 'https://accountss-3.onrender.com/api/accounts2';

const ServiceAgreement = () => {
  // State Management
  const [agreements, setAgreements] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedAgreement, setExpandedAgreement] = useState(null);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [showNewAgreement, setShowNewAgreement] = useState(false);
  const [showEditAgreement, setShowEditAgreement] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [clusters, setClusters] = useState([]);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    paid: 0,
    unpaid: 0,
    totalValue: 0
  });

  const itemsPerPage = 30;

  // Form States
  const initialFormState = {
    po: '',
    project_name: '',
    cluster: '',
    type_of_service: '',
    location: '',
    contract_start_date: '',
    contract_end_date: '',
    contract_value: '',
    bank: '',
    unit_price: '',
    vat_percentage: 5,
    vat_value: '',
    vat_monthly: ''
  };

  const [formData, setFormData] = useState(initialFormState);
  const [editFormData, setEditFormData] = useState(initialFormState);

  const SERVICE_TYPES = [
    'Landscaping & Irrigation System Maintenance',
    'Swimming Pool Cleaning & Maintenance'
  ];

  const BANKS = ['ADCB', 'BBF'];

  // Utility Functions
  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric',
        month: 'short'
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatDateInput = (dateString) => {
    if (!dateString) return '';
    try {
      const [month, day, year] = dateString.split('/');
      return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
    } catch (error) {
      return dateString;
    }
  };

  // API Calls
  const fetchAgreements = async () => {
    try {
      setLoading(true);
      console.log('Fetching agreements...');
      const response = await axios.get(`${API_BASE_URL}/agreements`);
      
      if (response.data.success) {
        console.log('Raw API response:', response.data.data);

        const processedData = response.data.data.reduce((acc, item) => {
          if (!acc[item.po]) {
            acc[item.po] = {
              ...item,
              invoices: []
            };
          }

          if (item.invoices && Array.isArray(item.invoices)) {
            acc[item.po].invoices = item.invoices;
          }

          return acc;
        }, {});

        console.log('Processed agreements:', processedData);
        setAgreements(processedData);

        // Calculate stats
        let totalInvoices = 0;
        let paidInvoices = 0;
        let totalValue = 0;

        Object.values(processedData).forEach(agreement => {
          if (agreement.invoices && Array.isArray(agreement.invoices)) {
            totalInvoices += agreement.invoices.length;
            paidInvoices += agreement.invoices.filter(inv => inv.amount_paid > 0).length;
          }
          totalValue += Number(agreement.contract_value) || 0;
        });

        setStats({
          total: totalInvoices,
          paid: paidInvoices,
          unpaid: totalInvoices - paidInvoices,
          totalValue: totalValue
        });
      }
    } catch (err) {
      console.error('Error fetching agreements:', err);
      setError('Failed to load agreements');
    } finally {
      setLoading(false);
    }
  };

  const fetchClusters = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/clusters`);
      if (response.data.success) {
        setClusters(response.data.clusters);
      }
    } catch (err) {
      console.error('Error fetching clusters:', err);
    }
  };

  // Excel Import Handler
  const handleExcelImport = async (e) => {
  try {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const workbook = XLSX.read(event.target.result, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(worksheet);

        // Rest of your code remains the same...
      } catch (err) {
        console.error('Error processing excel:', err);
        setError(`Failed to process excel file: ${err.message}`);
      }
    };

    reader.readAsBinaryString(file);
  } catch (err) {
    console.error('Error importing excel:', err);
    setError(`Failed to import excel file: ${err.message}`);
  }
};

  // Form Handlers
  const handleCreateAgreement = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_BASE_URL}/agreements`, {
        ...formData,
        pin: '2029'
      });

      if (response.data.success) {
        await fetchAgreements();
        setShowNewAgreement(false);
        setFormData(initialFormState);
        setError(null);
      }
    } catch (err) {
      console.error('Error creating agreement:', err);
      setError(`Failed to create agreement: ${err.message}`);
    }
  };

  const handleEditAgreement = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(
        `${API_BASE_URL}/agreements/${editFormData.po}`,
        {
          ...editFormData,
          pin: '2029'
        }
      );

      if (response.data.success) {
        await fetchAgreements();
        setShowEditAgreement(false);
        setSelectedAgreement(null);
        setError(null);
      }
    } catch (err) {
      console.error('Error updating agreement:', err);
      setError(`Failed to update agreement: ${err.message}`);
    }
  };

  const handleDeleteAgreement = async () => {
    try {
      if (!selectedAgreement?.po) {
        setError('No agreement selected for deletion');
        return;
      }

      const response = await axios.delete(
        `${API_BASE_URL}/agreements/${selectedAgreement.po}`,
        {
          data: { pin: '2029' }
        }
      );

      if (response.data.success) {
        await fetchAgreements();
        setShowDeleteConfirm(false);
        setSelectedAgreement(null);
        setError(null);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        // If agreement not found in database, remove from frontend state
        setAgreements(prev => {
          const newAgreements = { ...prev };
          delete newAgreements[selectedAgreement.po];
          return newAgreements;
        });
        setShowDeleteConfirm(false);
        setSelectedAgreement(null);
        setError(null);
      } else {
        console.error('Error deleting agreement:', err);
        setError(`Failed to delete agreement: ${err.message}`);
      }
    }
  };

  // Filter and Pagination
  const filteredAgreements = Object.values(agreements)
    .filter(agreement => {
      const searchLower = searchTerm.toLowerCase().trim();
      return (
        agreement.po?.toLowerCase().includes(searchLower) ||
        agreement.project_name?.toLowerCase().includes(searchLower) ||
        agreement.cluster?.toLowerCase().includes(searchLower) ||
        agreement.type_of_service?.toLowerCase().includes(searchLower) ||
        agreement.location?.toLowerCase().includes(searchLower) ||
        (agreement.invoices || []).some(invoice => 
          invoice.invoice_no?.toLowerCase().includes(searchLower)
        )
      );
    });

  const paginatedAgreements = filteredAgreements
    .slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const totalPages = Math.ceil(filteredAgreements.length / itemsPerPage);

  // Initial Data Load
  useEffect(() => {
    const debugAgreements = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/agreements`);
        console.log('Debug - Raw API response:', response.data);
      } catch (err) {
        console.error('Debug - API error:', err);
      }
    };

    debugAgreements();
    fetchAgreements();
    fetchClusters();
  }, []);

  // Render invoice card component
  const renderInvoiceCard = (invoice) => (
    <div
      key={invoice.invoice_no}
      className={`p-4 rounded-lg border
        ${invoice.amount_paid > 0 ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}
    >
      <div className="flex justify-between items-center">
        <span className="font-medium">{invoice.invoice_no}</span>
        <span className={`px-2 py-1 rounded-full text-xs
          ${invoice.amount_paid > 0 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}
        >
          {invoice.amount_paid > 0 ? 'Paid' : 'Unpaid'}
        </span>
      </div>
      <div className="mt-2 text-sm text-gray-600 space-y-1">
        <div>Month: {formatDate(invoice.month)}</div>
        <div>Amount: {formatCurrency(invoice.amount_paid || invoice.amount)}</div>
        {invoice.amount_paid > 0 && (
          <div className="flex items-center gap-2">
            <span>Bank:</span>
            <span className={`px-2 py-0.5 rounded text-xs
              ${invoice.adcb > 0 ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}
            >
              {invoice.adcb > 0 ? 'ADCB' : 'BBF'}
            </span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="p-4">
      {/* Header Section */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-6">
          <div className="text-sm flex gap-2 items-center">
            <span className="font-medium text-gray-600">Total:</span>
            <span className="font-bold text-gray-900">{stats.total}</span>
          </div>
          <div className="text-sm flex gap-2 items-center">
            <span className="font-medium text-green-600">Paid:</span>
            <span className="font-bold text-green-600">{stats.paid}</span>
          </div>
          <div className="text-sm flex gap-2 items-center">
            <span className="font-medium text-yellow-600">Unpaid:</span>
            <span className="font-bold text-yellow-600">{stats.unpaid}</span>
          </div>
          <div className="text-sm flex gap-2 items-center">
            <span className="font-medium text-gray-600">Total Value:</span>
            <span className="font-bold text-gray-900">{formatCurrency(stats.totalValue)}</span>
          </div>
        </div>
        <div className="flex gap-4">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search agreements..."
              className="pl-8 w-80"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <Button onClick={() => setShowNewAgreement(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Agreement
          </Button>
          <div>
            <input
              type="file"
              id="excel-import"
              className="hidden"
              accept=".xlsx,.xls"
              onChange={handleExcelImport}
            />
            <Button variant="outline" onClick={() => document.getElementById('excel-import').click()}>
              <Upload className="h-4 w-4 mr-2" />
              Import Excel
            </Button>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Main Table */}
      <div className="border rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px]">Agreement No</TableHead>
              <TableHead>Project Name</TableHead>
              <TableHead>Cluster</TableHead>
              <TableHead>Service Type</TableHead>
              <TableHead>Start Date</TableHead>
              <TableHead>End Date</TableHead>
              <TableHead>Value</TableHead>
              <TableHead className="text-center">Invoices</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8">
                  Loading agreements...
                </TableCell>
              </TableRow>
            ) : paginatedAgreements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8">
                  No agreements found
                </TableCell>
              </TableRow>
            ) : (
              paginatedAgreements.map((agreement) => (
                <React.Fragment key={agreement.po}>
                  <TableRow>
                    <TableCell>
                      <button
                        onClick={() => setExpandedAgreement(
                          expandedAgreement === agreement.po ? null : agreement.po
                        )}
                        className="text-blue-600 hover:text-blue-800 underline flex items-center"
                      >
                        {agreement.po}
                        {expandedAgreement === agreement.po ? 
                          <ChevronUp className="ml-2 h-4 w-4" /> : 
                          <ChevronDown className="ml-2 h-4 w-4" />
                        }
                      </button>
                    </TableCell>
                    <TableCell>{agreement.project_name}</TableCell>
                    <TableCell>{agreement.cluster}</TableCell>
                    <TableCell>{agreement.type_of_service}</TableCell>
                    <TableCell>{formatDate(agreement.contract_start_date)}</TableCell>
                    <TableCell>{formatDate(agreement.contract_end_date)}</TableCell>
                    <TableCell>{formatCurrency(agreement.contract_value)}</TableCell>
                    <TableCell className="text-center">
                      {agreement.invoices?.length || 0}
                    </TableCell>
                  </TableRow>
                  {expandedAgreement === agreement.po && (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <div className="p-4 bg-gray-50">
                          <div className="flex justify-between mb-4">
                            <h4 className="font-medium">Invoices</h4>
                            <div className="flex gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => {
                                  setSelectedAgreement(agreement);
                                  setEditFormData(agreement);
                                  setShowEditAgreement(true);
                                }}
                              >
                                <Edit className="h-4 w-4 mr-2" />
                                Edit Agreement
                              </Button>
                              <Button 
                                variant="destructive" 
                                size="sm"
                                onClick={() => {
                                  setSelectedAgreement(agreement);
                                  setShowDeleteConfirm(true);
                                }}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Agreement
                              </Button>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-4">
                            {agreement.invoices && Array.isArray(agreement.invoices) && 
                             agreement.invoices.length > 0 ? (
                              agreement.invoices.map(renderInvoiceCard)
                            ) : (
                              <div className="col-span-3 text-center py-4 text-gray-500">
                                No invoices available for this agreement
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center mt-4">
        <span className="text-sm text-gray-600">
          Showing {((page - 1) * itemsPerPage) + 1} to{' '}
          {Math.min(page * itemsPerPage, filteredAgreements.length)} of{' '}
          {filteredAgreements.length} entries
        </span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="px-4 py-2 flex items-center">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => p + 1)}
            disabled={page >= totalPages}
          >
            Next
          </Button>
        </div>
      </div>

      {/* New Agreement Modal */}
      <Dialog open={showNewAgreement} onOpenChange={setShowNewAgreement}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Create New Agreement</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateAgreement}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="po">Agreement Number (PO)</Label>
                  <Input
                    id="po"
                    value={formData.po}
                    onChange={(e) => setFormData({ ...formData, po: e.target.value })}
                    required
                    className="mt-1"
                    placeholder="Enter PO number"
                  />
                </div>
                <div>
                  <Label htmlFor="project_name">Project Name</Label>
                  <Input
                    id="project_name"
                    value={formData.project_name}
                    onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                    required
                    className="mt-1"
                    placeholder="Enter project name"
                  />
                </div>
                <div>
                  <Label htmlFor="cluster">Cluster</Label>
                  <Select
                    value={formData.cluster}
                    onValueChange={(value) => setFormData({ ...formData, cluster: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select cluster" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] overflow-y-auto">
                      {clusters.map((cluster) => (
                        <SelectItem key={cluster} value={cluster}>
                          {cluster}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="type_of_service">Service Type</Label>
                  <Select
                    value={formData.type_of_service}
                    onValueChange={(value) => setFormData({ ...formData, type_of_service: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select service type" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    required
                    className="mt-1"
                    placeholder="Enter location"
                  />
                </div>
                <div>
                  <Label htmlFor="bank">Bank</Label>
                  <Select
                    value={formData.bank}
                    onValueChange={(value) => setFormData({ ...formData, bank: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select bank" />
                    </SelectTrigger>
                    <SelectContent>
                      {BANKS.map((bank) => (
                        <SelectItem key={bank} value={bank}>{bank}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="contract_start_date">Start Date</Label>
                  <Input
                    id="contract_start_date"
                    type="text"
                    value={formData.contract_start_date}
                    onChange={(e) => setFormData({ ...formData, contract_start_date: e.target.value })}
                    required
                    className="mt-1"
                    placeholder="MM/DD/YYYY"
                  />
                </div>
                <div>
                  <Label htmlFor="contract_end_date">End Date</Label>
                  <Input
                    id="contract_end_date"
                    type="text"
                    value={formData.contract_end_date}
                    onChange={(e) => setFormData({ ...formData, contract_end_date: e.target.value })}
                    required
                    className="mt-1"
                    placeholder="MM/DD/YYYY"
                  />
                </div>
                <div>
                  <Label htmlFor="contract_value">Contract Value</Label>
                  <Input
                    id="contract_value"
                    type="number"
                    step="0.01"
                    value={formData.contract_value}
                    onChange={(e) => setFormData({ ...formData, contract_value: e.target.value })}
                    required
                    className="mt-1"
                    placeholder="Enter contract value"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowNewAgreement(false)}>
                Cancel
              </Button>
              <Button type="submit">Create Agreement</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Agreement Modal */}
      <Dialog open={showEditAgreement} onOpenChange={setShowEditAgreement}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Agreement</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditAgreement}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Agreement Number (PO)</Label>
                  <Input
                    value={editFormData.po}
                    onChange={(e) => setEditFormData({ ...editFormData, po: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Project Name</Label>
                  <Input
                    value={editFormData.project_name}
                    onChange={(e) => setEditFormData({ ...editFormData, project_name: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Cluster</Label>
                  <Select
                    value={editFormData.cluster}
                    onValueChange={(value) => setEditFormData({ ...editFormData, cluster: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select cluster" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] overflow-y-auto">
                      {clusters.map((cluster) => (
                        <SelectItem key={cluster} value={cluster}>
                          {cluster}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Service Type</Label>
                  <Select
                    value={editFormData.type_of_service}
                    onValueChange={(value) => setEditFormData({ ...editFormData, type_of_service: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select service type" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Location</Label>
                  <Input
                    value={editFormData.location}
                    onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Bank</Label>
                  <Select
                    value={editFormData.bank}
                    onValueChange={(value) => setEditFormData({ ...editFormData, bank: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select bank" />
                    </SelectTrigger>
                    <SelectContent>
                      {BANKS.map((bank) => (
                        <SelectItem key={bank} value={bank}>
                          {bank}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="text"
                    value={formatDateInput(editFormData.contract_start_date)}
                    onChange={(e) => setEditFormData({
                      ...editFormData,
                      contract_start_date: e.target.value
                    })}
                    placeholder="MM/DD/YYYY"
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="text"
                    value={formatDateInput(editFormData.contract_end_date)}
                    onChange={(e) => setEditFormData({
                      ...editFormData,
                      contract_end_date: e.target.value
                    })}
                    placeholder="MM/DD/YYYY"
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Contract Value</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editFormData.contract_value}
                    onChange={(e) => setEditFormData({ ...editFormData, contract_value: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Show calculated values */}
              {editFormData.contract_value && editFormData.contract_start_date && editFormData.contract_end_date && (
                <div className="grid grid-cols-2 gap-4 mt-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <Label>Monthly Value</Label>
                    <div className="text-sm font-medium mt-1">
                      {formatCurrency(editFormData.unit_price)}
                    </div>
                  </div>
                  <div>
                    <Label>VAT Amount (5%)</Label>
                    <div className="text-sm font-medium mt-1">
                      {formatCurrency(editFormData.vat_value)}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditAgreement(false)}>
                Cancel
              </Button>
              <Button type="submit">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Agreement</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-700">
              Are you sure you want to delete agreement <span className="font-medium">{selectedAgreement?.po}</span>?
            </p>
            <p className="text-sm text-gray-500 mt-2">
              This action cannot be undone and will remove all associated invoices.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteAgreement}>
              Delete Agreement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Excel Import Instructions Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Invoice Data</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600 mb-4">
              Please ensure your Excel file has the following columns:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 mb-4">
              <li>invoice_no (e.g., LOAMS-0001)</li>
              <li>amount_paid</li>
              <li>adcb (amount paid through ADCB)</li>
              <li>bbf (amount paid through BBF)</li>
              <li>month (in MM/DD/YYYY format)</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportModal(false)}>
              Cancel
            </Button>
            <Button onClick={() => document.getElementById('excel-import').click()}>
              Select File
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceAgreement;



      

  



